import { useCallback } from "react";
import { useGetContext, useGetConfig } from "@onefront/forrestjs-react-root";
import { useAuth } from "../../service-auth/state/use-auth";
import { useOneFrontStore } from "../../service-state/store/onefront-store";

import { createProxyUrl } from "../utils/create-proxy-url";

export const useCreateJumpUrl = () => {
  const proxyBaseUrl = useGetContext("one.axios.proxy.baseUrl");
  const urlKey = useGetConfig("one.state.ctx.param.key", "ctx");
  const { sessionData } = useAuth();
  const sharedContext = useOneFrontStore((state) => state.sessionData);

  /**
   * @param {string} target - URN (or URL as well) to be converted into a proxy url which
   *   first go throught loginApi to create a new session for the target app
   *
   * @returns {string} The proxy url to be called
   */
  return useCallback(
    (target) => {
      // Check if the target is a direct URL or a URN
      if (target.startsWith("http://") || target.startsWith("https://")) {
        const targetWithSession = addSessionValuesToTarget(
          target,
          sharedContext,
          urlKey
        );
        // Handle as a direct URL (bypass URN-specific processing)
        return createProxyUrl(targetWithSession, proxyBaseUrl);
      } else {
        const url = buildUrnToLoginApi(
          target,
          sessionData,
          sharedContext,
          urlKey
        );
        return createProxyUrl(url, proxyBaseUrl);
      }
    },
    [sessionData, proxyBaseUrl, sharedContext, urlKey]
  );
};

function buildUrnToLoginApi(
  target,
  sessionData,
  sharedContext,
  urlKey = "ctx"
) {
  const [appId, deepLink] = target.split("://");
  const data = sessionData ? { state: btoa(JSON.stringify(sessionData)) } : {};

  const urlParams = new URLSearchParams({
    ...{
      serviceId: appId
    },
    ...data
  });

  const deepLinkString = addSessionValuesToTarget(
    deepLink ? `/${deepLink}` : "",
    sharedContext,
    urlKey
  );

  if (deepLinkString) urlParams.append("deepLink", deepLinkString);

  return `loginApi://jump?${urlParams.toString()}`;
}

function addSessionValuesToTarget(target, sharedContext, urlKey = "ctx") {
  if (sharedContext) {
    const sharedContextString = encodeURIComponent(
      btoa(JSON.stringify(sharedContext))
    );

    return target.indexOf("?") >= 0
      ? `${target}&${urlKey}=${sharedContextString}`
      : `${target}?${urlKey}=${sharedContextString}`;
  }
  return target;
}
